import React, { useState, useCallback, useMemo } from 'react'
import {
  Box,
  Button,
  Chip,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Paper,
  Stack,
  Divider,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import SearchIcon from '@mui/icons-material/Search'
import { SearchBox } from '@/apps/legacy/components/portal/SearchBox'

import { AdvancedSearchCondition } from '@/shared/types/advancedSearch'
import { useDosageFormOptionsQuery } from '@/features/internal/drugs'
import { SECONDARY_ICON_BUTTON_STYLE } from '@/shared/constants/buttonStyles'

interface AdvancedSearchBoxProps {
  /**
   * 通常検索の値
   */
  normalSearchValue: string

  /**
   * 通常検索の値が変更されたときのコールバック
   */
  onNormalSearchChange: (value: string) => void

  /**
   * 通常検索実行時のコールバック
   */
  onNormalSearch: () => void

  /**
   * 高度検索実行時のコールバック
   */
  onAdvancedSearch: (conditions: AdvancedSearchCondition[]) => void

  /**
   * プレースホルダーテキスト
   */
  placeholder?: string

  /**
   * 高度検索モードかどうか（外部から制御）
   */
  isAdvancedMode?: boolean

  /**
   * 高度検索モード変更時のコールバック
   */
  onAdvancedModeChange?: (isAdvanced: boolean) => void

  /**
   * 初期の高度検索条件
   */
  initialAdvancedConditions?: AdvancedSearchCondition[]
}

const SEARCH_TYPES = [
  { value: 'yj_code', label: 'YJコード', field: 'yj_code' },
  { value: 'drug_name', label: '医薬品名', field: 'product_name' },
  { value: 'ingredient', label: '成分名', field: 'general_name' },
  { value: 'dosage_form', label: '剤形', field: 'dosage_form' },
] as const

const OPERATORS = [
  { value: 'and', label: 'かつ (AND)' },
  { value: 'or', label: 'または (OR)' },
  { value: 'exclude', label: '除外 (NOT)' },
] as const

/**
 * 通常検索と高度検索を切り替え可能な検索コンポーネント
 */
export const AdvancedSearchBox: React.FC<AdvancedSearchBoxProps> = ({
  normalSearchValue,
  onNormalSearchChange,
  onNormalSearch,
  onAdvancedSearch,
  placeholder = '医薬品名またはYJコードで検索',
  isAdvancedMode: controlledIsAdvancedMode,
  onAdvancedModeChange,
  initialAdvancedConditions,
}) => {
  const [internalIsAdvancedMode, setInternalIsAdvancedMode] = useState(false)
  const [conditionIdCounter, setConditionIdCounter] = useState(1)

  // 専用フックで剤形オプション取得
  const { dosageFormOptions } = useDosageFormOptionsQuery()

  // 親から制御される場合はそれを使用、そうでなければ内部状態を使用
  const isAdvancedMode =
    controlledIsAdvancedMode !== undefined
      ? controlledIsAdvancedMode
      : internalIsAdvancedMode
  const setIsAdvancedMode = onAdvancedModeChange || setInternalIsAdvancedMode
  const [isExpanded, setIsExpanded] = useState(false)

  // 初期条件が提供された場合はそれを使用、そうでなければデフォルト条件
  // initialAdvancedConditions を key として使用することで、親から新しい条件が渡された際に状態をリセット
  const getInitialConditions = (): AdvancedSearchCondition[] =>
    initialAdvancedConditions && initialAdvancedConditions.length > 0
      ? initialAdvancedConditions
      : [
          {
            id: 'condition-0',
            type: 'drug_name' as const,
            field: 'product_name',
            value: '',
            operator: 'and' as const,
          },
        ]

  const [conditions, setConditions] =
    useState<AdvancedSearchCondition[]>(getInitialConditions)

  // initialAdvancedConditions が変更された際の同期には useState の lazy initialization を活用
  // ただし、初期化後の変更を反映するため、明示的な key ベースのリセットが必要な場合は
  // 親コンポーネントで key prop を使用してコンポーネントを再マウントすることを推奨

  // 検索条件を追加
  const addCondition = useCallback(() => {
    setConditions((prev) => {
      const newId = `condition-${conditionIdCounter}`
      const newCondition: AdvancedSearchCondition = {
        id: newId,
        type: 'drug_name',
        field: 'product_name',
        value: '',
        operator: 'and',
      }
      return [...prev, newCondition]
    })
    setConditionIdCounter((prev) => prev + 1)
  }, [conditionIdCounter])

  // 検索条件を削除
  const removeCondition = useCallback((id: string) => {
    setConditions((prev) => prev.filter((condition) => condition.id !== id))
  }, [])

  // 検索条件を更新
  const updateCondition = useCallback(
    (id: string, updates: Partial<AdvancedSearchCondition>) => {
      setConditions((prev) =>
        prev.map((condition) => {
          if (condition.id !== id) return condition

          const updated = { ...condition, ...updates }

          // タイプが変更された場合はフィールドも更新
          if (updates.type) {
            const typeConfig = SEARCH_TYPES.find(
              (t) => t.value === updates.type
            )
            if (typeConfig) {
              updated.field = typeConfig.field
            }
          }

          return updated
        })
      )
    },
    // 空の依存配列 - この関数は外部変数に依存せず、純粋にstateの更新のみを行うため
    []
  )

  // 有効な検索条件をメモ化
  const validConditions = useMemo(
    () => conditions.filter((condition) => condition.value.trim()),
    [conditions]
  )

  // 高度検索実行
  const handleAdvancedSearch = useCallback(() => {
    if (validConditions.length > 0) {
      // 高度検索実行時は、検索条件と展開状態を保持したまま実行
      // モード変更は親コンポーネントに委ねる（不要な状態変更を避ける）
      onAdvancedSearch(validConditions)
    }
  }, [validConditions, onAdvancedSearch])

  // 通常検索モードに戻る
  const switchToNormalMode = useCallback(() => {
    setIsAdvancedMode(false)
    setIsExpanded(false)
  }, [setIsAdvancedMode])

  // 高度検索モードに切り替え
  const switchToAdvancedMode = useCallback(() => {
    setIsAdvancedMode(true)
    setIsExpanded(true)
  }, [setIsAdvancedMode])

  // 高度検索モードの変更を isExpanded に派生させる
  // isAdvancedMode が true になったら自動的に展開する（派生状態として計算）
  const shouldBeExpanded = isAdvancedMode || isExpanded

  // isExpanded の実際の値を shouldBeExpanded と同期
  // ただし、これも useEffect での setState になるため、代わりに派生状態を直接使用する

  return (
    <Paper
      elevation={0}
      sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}
    >
      {/* 検索モード切り替えボタン */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Stack direction="row" spacing={1}>
          <Chip
            label="通常検索"
            variant={!isAdvancedMode ? 'filled' : 'outlined'}
            color={!isAdvancedMode ? 'primary' : 'default'}
            onClick={switchToNormalMode}
            clickable
          />
          <Chip
            label="高度検索"
            variant={isAdvancedMode ? 'filled' : 'outlined'}
            color={isAdvancedMode ? 'primary' : 'default'}
            onClick={switchToAdvancedMode}
            clickable
          />
        </Stack>

        {isAdvancedMode && (
          <IconButton
            onClick={() => setIsExpanded(!shouldBeExpanded)}
            size="small"
            aria-label={
              shouldBeExpanded ? '高度検索を折りたたむ' : '高度検索を展開'
            }
          >
            {shouldBeExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        )}
      </Box>

      {/* 通常検索 */}
      {!isAdvancedMode && (
        <SearchBox
          placeholder={placeholder}
          value={normalSearchValue}
          onChange={onNormalSearchChange}
          onSearch={onNormalSearch}
          buttonText="検索"
        />
      )}

      {/* 高度検索 */}
      {isAdvancedMode && (
        <Collapse in={shouldBeExpanded}>
          <Box sx={{ mt: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              検索条件を指定
            </Typography>

            <Stack spacing={2}>
              {conditions.map((condition, index) => (
                <Paper key={condition.id} variant="outlined" sx={{ p: 2 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {/* 検索対象選択 */}
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>検索対象</InputLabel>
                      <Select
                        value={condition.type}
                        label="検索対象"
                        onChange={(e) =>
                          updateCondition(condition.id, {
                            type: e.target
                              .value as AdvancedSearchCondition['type'],
                          })
                        }
                      >
                        {SEARCH_TYPES.map((type) => (
                          <MenuItem key={type.value} value={type.value}>
                            {type.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* 検索値入力 */}
                    <Box sx={{ flex: 1 }}>
                      {condition.type === 'dosage_form' ? (
                        <FormControl size="small" fullWidth>
                          <InputLabel>剤形</InputLabel>
                          <Select
                            value={condition.value}
                            label="剤形"
                            onChange={(e) =>
                              updateCondition(condition.id, {
                                value: e.target.value,
                              })
                            }
                          >
                            {Array.isArray(dosageFormOptions) &&
                              dosageFormOptions.map((option) => (
                                <MenuItem
                                  key={option.value}
                                  value={option.value}
                                >
                                  {option.label}
                                </MenuItem>
                              ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <TextField
                          size="small"
                          fullWidth
                          placeholder={
                            condition.type === 'yj_code'
                              ? 'YJコードを入力 (例: 1124)'
                              : condition.type === 'drug_name'
                              ? '医薬品名を入力'
                              : '成分名を入力'
                          }
                          value={condition.value}
                          onChange={(e) =>
                            updateCondition(condition.id, {
                              value: e.target.value,
                            })
                          }
                        />
                      )}
                    </Box>

                    {/* 論理演算子選択 (最初の条件以外) */}
                    {index > 0 && (
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>条件</InputLabel>
                        <Select
                          value={condition.operator}
                          label="条件"
                          onChange={(e) =>
                            updateCondition(condition.id, {
                              operator: e.target
                                .value as AdvancedSearchCondition['operator'],
                            })
                          }
                        >
                          {OPERATORS.map((operator) => (
                            <MenuItem
                              key={operator.value}
                              value={operator.value}
                            >
                              {operator.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}

                    {/* 削除ボタン (複数条件がある場合のみ) */}
                    {conditions.length > 1 && (
                      <IconButton
                        onClick={() => removeCondition(condition.id)}
                        size="small"
                        aria-label="この条件を削除"
                        sx={SECONDARY_ICON_BUTTON_STYLE}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>
                </Paper>
              ))}
            </Stack>
            <Divider sx={{ my: 2 }} />
            {/* アクションボタン */}
            <Stack direction="row" spacing={2} justifyContent="space-between">
              <Button
                startIcon={<AddIcon />}
                onClick={addCondition}
                variant="outlined"
                size="small"
                sx={{
                  py: 0.5,
                  px: 1.5,
                  color: 'text.primary',
                  borderColor: 'text.secondary',
                  '&:hover': {
                    borderColor: 'text.primary',
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                条件を追加
              </Button>

              <Button
                startIcon={<SearchIcon />}
                onClick={handleAdvancedSearch}
                variant="contained"
                color="primary"
                size="small"
                disabled={conditions.every((c) => !c.value.trim())}
              >
                検索実行
              </Button>
            </Stack>
          </Box>
        </Collapse>
      )}
    </Paper>
  )
}

export default AdvancedSearchBox
